import { useEffect, useMemo, useRef, useState } from 'react'
import { supabase } from './supabase'
import { buildComplianceState, logClientEmailAction } from './compliance'

const BM = {
  name: 'Benchmark Insurance Group Pty Ltd',
  trading: 'Benchmark Insurance Brokers',
  abn: '33 694 361 549',
  ar: '001320555',
  phone: '1300 553 519',
  email: 'info@benchmarkinsurance.com.au',
  website: 'https://benchmarkinsurance.com.au/',
  licensee: 'McLardy McShane Partners Pty Ltd',
  lic_abn: '14 064 465 309',
  lic_afsl: '232987',
}

const FSG_URL            = 'https://benchmarkinsurance.com.au/wp-content/uploads/2026/02/Benchmark-FSG-Mar-26.pdf'
const TERMS_URL          = 'https://benchmarkinsurance.com.au/wp-content/uploads/2026/02/Benchmark-Terms-of-Engagement-Mar-26.pdf'
const PRIVACY_URL        = 'https://benchmarkinsurance.com.au/wp-content/uploads/2026/02/Benchmark-Privacy-Policy-Mar-26.pdf'
const CODE_URL           = 'https://niba.com.au/code-of-practice'
const COMPLAINTS_URL     = 'https://www.mclardymcshane.com.au/complaints-dispute-handling'
const IMPORTANT_INFO_URL = 'https://benchmarkinsurance.com.au/wp-content/uploads/2026/02/Benchmark-Important-Information-Mar-26.pdf'
const WEBSITE_URL        = 'https://benchmarkinsurance.com.au/'
const BRAND_GREEN        = '#1e3d30'
const LOGO_URL           = 'https://dtdwmzviywqumpouqula.supabase.co/storage/v1/object/public/assets/BIB-Dark-(Horizontal).png'

const RETAIL_TYPES      = ['Car','Commercial Motor','Motorbike','Caravan','Boat','Property','Landlord','Farm','Home & Contents']
const RETAIL_EXACT      = ['PAC']
const CLAIMS_MADE_TYPES = ['Professional Indemnity','Management Liability','Cyber','Directors & Officers']
const MONTHLY_LOADING_RATE = 0.11
const isRetailType  = (ct) => RETAIL_TYPES.some(t => (ct||'').toLowerCase().includes(t.toLowerCase())) || RETAIL_EXACT.some(t => (ct||'').trim().toUpperCase() === t)
const isClaimsMadeType = (ct) => CLAIMS_MADE_TYPES.some(t => (ct||'').toLowerCase().includes(t.toLowerCase()))

const DOC_TYPES = [
  { key: 'invoice',        label: 'Tax Invoice & Policy Schedule' },
  { key: 'record',         label: 'Record of Answers' },
  { key: 'fsg',            label: 'Financial Services Guide (FSG)', linkOnly: true },
  { key: 'coc',            label: 'Certificate of Currency (COC)' },
  { key: 'pds',            label: 'Product Disclosure Statement (PDS)' },
  { key: 'benchmark_info', label: 'Benchmark Important Information', linkOnly: true },
  { key: 'confirmation',   label: 'Confirmation of Placement' },
  { key: 'supplement',     label: 'Supplement Document' },
]

const CC_COVER_TYPES = [
  'Truck','Commercial Motor','Car','Plant & Equipment','Commercial Property',
  'Landlord','Public Liability','Professional Indemnity','Management Liability',
  'Carriers','Construction','Farm','Boat','Workers Compensation','PAC',
  'Home & Contents','Caravan','Motorbike','Residential Strata','Marine',
  'Cyber','Travel','Business Package','Other',
]

const ENDORSEMENT_TYPES = [
  'Add a vehicle / asset','Remove a vehicle / asset','Change sum insured',
  'Add an interested party / financier','Remove an interested party',
  'Change address','Add a driver','Remove a driver','Change business activity',
  'Add an extension / cover','Remove an extension / cover',
  'Change building construction','Change occupation / use',
  'Update interested party details','Other',
]

const INSURER_LIST = [
  'Allianz Australia Insurance Limited',
  'CGU Insurance Limited',
  'Vero Insurance Limited',
  'QBE Insurance (Australia) Limited',
  'Zurich Australian Insurance Limited',
  'Chubb Insurance Australia Limited',
  'Liberty Specialty Markets',
  'HDI Global Specialty SE – Australia',
  'Hollard Commercial Insurance',
  'Hollard Personal Insurance',
  'Ansvar Insurance Limited',
  'NTI Limited',
  'AIG Australia Limited',
  'Berkshire Hathaway Specialty Insurance (BHSI)',
  'Arch Insurance Australia',
  'Pacific International Insurance',
  'RelyOn Insurance',
  'Victor Insurance Australia',
  'Guild Insurance Limited',
  'Protecsure',
  'Blue Zebra Insurance',
  'Allstate Underwriting',
  'Clover Insurance',
  'Hutch Underwriting',
  'High Street Underwriting',
  'Coast Insurance',
  'Castle Insurance',
  'Market Lane Insurance Group',
  'UAA',
  'ProRisk',
  'GT Insurance',
  'PEN Underwriting',
  'ATL Insurance',
  'National Transport Insurance',
  'Other',
]

const BROKER_PHONES  = { 'Ben Rogers':'07 3666 8522','Jesse Roach':'07 2150 1260','Mitch Speer':'07 2150 1297' }
const BROKER_EMAILS  = { 'Ben Rogers':'ben@benchmarkinsurance.com.au','Jesse Roach':'jesse@benchmarkinsurance.com.au','Mitch Speer':'mitch@benchmarkinsurance.com.au' }
const BROKER_TITLES  = { 'Ben Rogers':'Principal','Jesse Roach':'Co-Founder and Director','Mitch Speer':'Co-Founder and Director' }

const fmtDate        = (v) => { if (!v) return '—'; return new Date(v).toLocaleDateString('en-AU',{day:'numeric',month:'long',year:'numeric'}) }
const parseCurr      = (v) => (v||'').replace(/[^\d.]/g,'')
const formatCurr     = (v) => { const n=Number(parseCurr(v)); if(!Number.isFinite(n)||n<=0) return ''; return n.toLocaleString('en-AU',{style:'currency',currency:'AUD',minimumFractionDigits:2,maximumFractionDigits:2}) }
const formatCurrNumber = (n) => { if(!Number.isFinite(n)||n<=0) return ''; return n.toLocaleString('en-AU',{style:'currency',currency:'AUD',minimumFractionDigits:2,maximumFractionDigits:2}) }
const getLoadedTotal = (v) => { const n=Number(parseCurr(v)); if(!Number.isFinite(n)||n<=0) return ''; return formatCurrNumber(n * (1 + MONTHLY_LOADING_RATE)) }
const getInstallment = (v, divisor=12) => { const n=Number(parseCurr(v)); if(!Number.isFinite(n)||n<=0) return ''; return formatCurrNumber((n * (1 + MONTHLY_LOADING_RATE)) / divisor) }
const addOneYear     = (d) => { if(!d) return ''; const dt=new Date(d); if(isNaN(dt)) return ''; dt.setFullYear(dt.getFullYear()+1); return dt.toISOString().split('T')[0] }

const FREQ_OPTIONS = { monthly: { label: 'Monthly', divisor: 12 }, fortnightly: { label: 'Fortnightly', divisor: 24 }, weekly: { label: 'Weekly', divisor: 48 } }

// ── Duty / disclosure HTML builders ──────────────────────────────────────────

function buildDutyHtml(coverType) {
  const retail = isRetailType(coverType)
  const claimsMade = isClaimsMadeType(coverType)

  const changesSection = `
<p style="margin:0 0 6px 0;font-size:14px;font-weight:700;color:#1a1a1a;">Changes to Your Information</p>
<p style="margin:0 0 20px 0;font-size:13px;line-height:1.7;color:#1a1a1a;">You must tell the insurer anything that is relevant to their decision to insure you. If your circumstances have changed, please let us know so that we may advise you whether you need to tell your insurer and/or change your insurance cover.</p>`

if (retail) {
    return `
<p style="margin:0 0 6px 0;font-size:14px;font-weight:700;color:#1a1a1a;">Target Market Determination</p>
<p style="margin:0 0 20px 0;font-size:13px;line-height:1.7;color:#1a1a1a;">This product has been issued in accordance with the insurer's Target Market Determination. Based on the information you have provided, we are satisfied that this product is consistent with your likely objectives, financial situation and needs. A copy of the TMD is available on request or on the insurer's website.</p>

<p style="margin:0 0 6px 0;font-size:14px;font-weight:700;color:#1a1a1a;">Duty to Take Reasonable Care Not to Make a Misrepresentation</p>
<p style="margin:0 0 20px 0;font-size:13px;line-height:1.7;color:#1a1a1a;">We remind you of your duty to take reasonable care not to make a misrepresentation. Full details are included in the Benchmark Important Information document attached. If you are in doubt about whether a particular matter should be disclosed, please contact your broker.</p>

${changesSection}

<p style="margin:0 0 6px 0;font-size:14px;font-weight:700;color:#1a1a1a;">Informed Consent — Commission Disclosure</p>
<p style="margin:0 0 20px 0;font-size:13px;line-height:1.7;color:#1a1a1a;">We are required by law to obtain your informed consent to receive commission before arranging your policy. In arranging this insurance, your nominated insurer will pay Benchmark Insurance Brokers a commission, typically between <strong>5% and 26.5%</strong> of the premium (excluding taxes and levies), paid <strong>annually</strong>. This consent applies to future renewals of this policy. We have confirmed your informed consent to us receiving this commission with you, and full details are included in our Terms of Engagement and FSG.</p>`
  }

  const claimsMadeSection = claimsMade ? `
<p style="margin:0 0 6px 0;font-size:14px;font-weight:700;color:#1a1a1a;">'Claims Made' Policy — Important Notice</p>
<p style="margin:0 0 20px 0;font-size:13px;line-height:1.7;color:#1a1a1a;">This is a <strong>'claims made'</strong> policy. It requires that any claim made against you, or any incident that could potentially give rise to a claim, must be notified to the insurer <strong>during the period of insurance</strong>. If you do not notify during the current period, you may have no cover for the claim. You cannot claim after the policy has lapsed or been cancelled unless you have arranged run-off cover. Please contact us immediately if you become aware of any circumstance that may give rise to a claim.</p>` : ''

return `
<p style="margin:0 0 6px 0;font-size:14px;font-weight:700;color:#1a1a1a;">Target Market Determination</p>
<p style="margin:0 0 20px 0;font-size:13px;line-height:1.7;color:#1a1a1a;">This product has been issued in accordance with the insurer's Target Market Determination. Based on the information you have provided, we are satisfied that this product is consistent with your likely objectives, financial situation and needs. A copy of the TMD is available on request or on the insurer's website.</p>
<p style="margin:0 0 6px 0;font-size:14px;font-weight:700;color:#1a1a1a;">Duty of Disclosure</p>
<p style="margin:0 0 20px 0;font-size:13px;line-height:1.7;color:#1a1a1a;">We remind you of your duty of disclosure. Full details are included in the Benchmark Important Information document attached. If you are in doubt about whether a particular matter should be disclosed, please contact your broker.</p>
${claimsMadeSection}
${changesSection}`
}

function buildPaymentHtml(coverData) {
  if (coverData.payment_type === 'monthly') {
    const freq = coverData.payment_frequency || 'monthly'
    const freqLabel = { monthly:'monthly', fortnightly:'fortnightly', weekly:'weekly' }[freq] || 'monthly'
    const freqDivisor = { monthly:12, fortnightly:24, weekly:48 }[freq] || 12
    const loadedTotal = getLoadedTotal(coverData.total_invoice)
    const installment = getInstallment(coverData.total_invoice, freqDivisor)

return `
<p style="margin:0 0 6px 0;font-size:14px;font-weight:700;color:#1a1a1a;">Premium Funding</p>
<p style="margin:0 0 20px 0;font-size:13px;line-height:1.7;color:#1a1a1a;">You have chosen to pay your premium by ${freqLabel} instalments via ${coverData.premium_funder||'your premium funder'}. You will receive separate correspondence directly from ${coverData.premium_funder||'your premium funder'} with your instalment schedule and payment details. We may receive a commission from the funder for arranging this; full details are available on request. Please be aware that if your policy is cancelled before the expiry date, the premium funder may charge the full interest applicable to the funding arrangement and there may be no refund of our commission or fees. Some insurers apply minimum premiums which may further reduce any refund. Please contact us before making any changes to a funded policy.</p>`
  }
  return `<p style="margin:0 0 20px 0;font-size:13px;line-height:1.7;color:#1a1a1a;">Payment is due by <strong>${fmtDate(coverData.payment_due_date)}</strong>. Please use the payment details on your invoice to keep your cover in force. If payment is not received by this date, we will be required to notify your insurer and your cover may be cancelled.</p>`
}

// ── Email HTML builder ───────────────────────────────────────────────────────

function buildCoverConfirmedHtml({ coverData, brokerName, brokerPhone, brokerTitle, isEndorsement }) {
  const attachedDocs = DOC_TYPES.filter(d => coverData.docs_attached?.[d.key])
  const allDocsHtml = (() => {
    const lines = []
    lines.push(`<li>Financial Services Guide (FSG) — <a href="${FSG_URL}" style="color:#1e3d30;">View document</a></li>`)
    lines.push(`<li>Benchmark Important Information — <a href="${IMPORTANT_INFO_URL}" style="color:#1e3d30;">View document</a></li>`)
    attachedDocs.filter(d => d.key !== 'fsg' && d.key !== 'benchmark_info').forEach(d => {
      lines.push(`<li>${d.label}</li>`)
    })
    return lines.join('')
  })()

  const periodText = coverData.period_from && coverData.period_to
    ? `${fmtDate(coverData.period_from)} to ${fmtDate(coverData.period_to)}` : '—'
  const freqDivisor = { monthly:12, fortnightly:24, weekly:48 }[coverData.payment_frequency] || 12
  const displayTotalInvoice = coverData.payment_type === 'monthly' ? (getLoadedTotal(coverData.total_invoice) || coverData.total_invoice) : coverData.total_invoice
  const displayInstallment = coverData.payment_type === 'monthly' ? getInstallment(coverData.total_invoice, freqDivisor) : ''

  const brokerSignoff = `<strong>${brokerName||'Your Broker'}</strong><br/>${brokerTitle?`${brokerTitle}<br/>`:''}${brokerPhone?`Direct: ${brokerPhone}<br/>`:''}<strong>Benchmark Insurance Brokers</strong><br/>${BM.phone} | <a href="mailto:${BM.email}" style="color:#1e3d30;">${BM.email}</a><br/><a href="${WEBSITE_URL}" style="color:#1e3d30;">benchmarkinsurance.com.au</a>`
  const footerLinks = `<a href="${FSG_URL}" style="color:#1e3d30;">Financial Services Guide</a> | <a href="${TERMS_URL}" style="color:#1e3d30;">Terms of Engagement</a> | <a href="${PRIVACY_URL}" style="color:#1e3d30;">Privacy Policy</a> | <a href="${CODE_URL}" style="color:#1e3d30;">Code of Practice</a> | <a href="${COMPLAINTS_URL}" style="color:#1e3d30;">Complaints &amp; Disputes</a>`

  const retail = isRetailType(coverData.cover_type||'')
  const claimsMade = isClaimsMadeType(coverData.cover_type||'')

  // ── Endorsement-specific email content ──
  const endorsementDetailsHtml = isEndorsement ? `
<p style="margin:0 0 10px 0;font-weight:700;">Endorsement Details</p>
<table role="presentation" width="100%" cellpadding="8" cellspacing="0" border="1" style="border-collapse:collapse;border-color:#cccccc;margin-bottom:18px;">
<tr><td style="background:#f5f5f5;width:38%;font-weight:700;">Endorsement Type</td><td>${coverData.endorsement_type||'—'}</td></tr>
<tr><td style="background:#f5f5f5;font-weight:700;">Description of Change</td><td>${(coverData.endorsement_description||'—').replace(/\n/g,'<br/>')}</td></tr>
<tr><td style="background:#f5f5f5;font-weight:700;">Reason for Endorsement</td><td>${(coverData.endorsement_reason||'—').replace(/\n/g,'<br/>')}</td></tr>
<tr><td style="background:#f5f5f5;font-weight:700;">Effective Date</td><td>${fmtDate(coverData.endorsement_effective_date)}</td></tr>
${coverData.adjustment_type ? (() => {
    let adjText = coverData.adjustment_type
    if (coverData.adjustment_type === 'Additional premium' && coverData.total_invoice) {
      adjText = coverData.payment_type === 'monthly'
        ? `Additional premium of <strong>${coverData.total_invoice}</strong> — added to ${coverData.premium_funder||'premium funder'} (${({'monthly':'Monthly','fortnightly':'Fortnightly','weekly':'Weekly'}[coverData.payment_frequency])||'Monthly'})`
        : `Additional premium of <strong>${coverData.total_invoice}</strong>`
    } else if (coverData.adjustment_type === 'Refund due' && coverData.total_invoice) {
      adjText = `Refund of <strong>${coverData.total_invoice}</strong>`
    }
    return `<tr><td style="background:#f5f5f5;font-weight:700;">Premium Adjustment</td><td>${adjText}</td></tr>`
  })() : ''}
</table>` : ''

  const introText = isEndorsement
    ? `We are pleased to confirm that the following endorsement has been processed on your policy as detailed below. Please review the updated documents carefully and contact us immediately if anything requires amendment.`
    : `We are pleased to confirm that cover has been arranged for you as detailed below. Please review the enclosed documents carefully and contact us immediately if anything requires amendment.`

  const emailTitle = isEndorsement ? 'Endorsement Confirmation' : 'Cover Confirmed'

  // Policy details table — endorsements show original policy ref
  const policyTableRows = isEndorsement ? `
<tr><td style="background:#f5f5f5;width:38%;font-weight:700;">Insured Name</td><td>${coverData.insured_name||'—'}</td></tr>
<tr><td style="background:#f5f5f5;font-weight:700;">Insurer</td><td>${coverData.insurer||'—'}</td></tr>
<tr><td style="background:#f5f5f5;font-weight:700;">Policy Number</td><td>${coverData.policy_number||'—'}</td></tr>
<tr><td style="background:#f5f5f5;font-weight:700;">Cover Type</td><td>${coverData.cover_type||'—'}</td></tr>
<tr><td style="background:#f5f5f5;font-weight:700;">Policy Period</td><td>${periodText}</td></tr>` : `
<tr><td style="background:#f5f5f5;width:38%;font-weight:700;">Insured Name</td><td>${coverData.insured_name||'—'}</td></tr>
<tr><td style="background:#f5f5f5;font-weight:700;">Insurer</td><td>${coverData.insurer||'—'}</td></tr>
<tr><td style="background:#f5f5f5;font-weight:700;">Policy Number</td><td>${coverData.policy_number||'—'}</td></tr>
<tr><td style="background:#f5f5f5;font-weight:700;">Cover Type</td><td>${coverData.cover_type_display||coverData.cover_type||'—'}</td></tr>
<tr><td style="background:#f5f5f5;font-weight:700;">Policy Period</td><td>${periodText}</td></tr>
<tr><td style="background:#f5f5f5;font-weight:700;">Payment Type</td><td>${coverData.payment_type === 'monthly' ? `${({'monthly':'Monthly','fortnightly':'Fortnightly','weekly':'Weekly'}[coverData.payment_frequency])||'Monthly'} via ${coverData.premium_funder||'Premium Funder'}` : 'Annual'}</td></tr>
<tr><td style="background:#f5f5f5;font-weight:700;">Annual Premium</td><td><strong>${coverData.total_invoice||'—'}</strong></td></tr>
${coverData.payment_type === 'monthly' ? `<tr><td style="background:#f5f5f5;font-weight:700;">Payment Method</td><td>Monthly instalments via ${coverData.premium_funder||'your premium funder'} — instalment details will be sent to you directly by the funder</td></tr>` : ''}`

  // For endorsements, show premium adjustment instead of full payment section
  const paymentSection = isEndorsement
    ? (coverData.adjustment_type && coverData.adjustment_type !== 'No change expected'
      ? (() => {
          if (coverData.adjustment_type === 'Additional premium') {
            if (coverData.payment_type === 'monthly') {
              const freq = coverData.payment_frequency || 'monthly'
              const freqLabel = { monthly:'monthly', fortnightly:'fortnightly', weekly:'weekly' }[freq] || 'monthly'
              const freqDiv = { monthly:12, fortnightly:24, weekly:48 }[freq] || 12
              const loadedTotal = getLoadedTotal(coverData.total_invoice)
              const installment = getInstallment(coverData.total_invoice, freqDiv)
return `<p style="margin:0 0 6px 0;font-size:14px;font-weight:700;color:#1a1a1a;">Premium Adjustment</p>
<p style="margin:0 0 20px 0;font-size:13px;line-height:1.7;color:#1a1a1a;">An additional premium of <strong>${coverData.total_invoice||'—'}</strong> applies to this endorsement. This will be added to your existing premium funding arrangement via ${coverData.premium_funder||'your premium funder'}. You will receive updated instalment details directly from ${coverData.premium_funder||'your premium funder'}.</p>`
            }
            return `<p style="margin:0 0 6px 0;font-size:14px;font-weight:700;color:#1a1a1a;">Premium Adjustment</p>
<p style="margin:0 0 20px 0;font-size:13px;line-height:1.7;color:#1a1a1a;">An additional premium of <strong>${coverData.total_invoice||'—'}</strong> applies to this endorsement. Please use the payment details on your updated invoice to finalise payment.</p>`
          }
          if (coverData.adjustment_type === 'Refund due') {
            return `<p style="margin:0 0 6px 0;font-size:14px;font-weight:700;color:#1a1a1a;">Premium Adjustment</p>
<p style="margin:0 0 20px 0;font-size:13px;line-height:1.7;color:#1a1a1a;">A premium refund of <strong>${coverData.total_invoice||'—'}</strong> will be processed for this endorsement. Please allow 5–10 business days for the refund to be applied.</p>`
          }
          return `<p style="margin:0 0 6px 0;font-size:14px;font-weight:700;color:#1a1a1a;">Premium Adjustment</p>
<p style="margin:0 0 20px 0;font-size:13px;line-height:1.7;color:#1a1a1a;">The premium impact of this endorsement is being confirmed. We will advise you of any adjustment shortly.</p>`
        })()
      : '')
    : buildPaymentHtml(coverData)

  return `<!DOCTYPE html>
<html xmlns="http://www.w3.org/1999/xhtml">
<head><meta charset="utf-8"/><meta name="viewport" content="width=device-width,initial-scale=1"/></head>
<body style="margin:0;padding:0;background:#f4f4f4;font-family:Arial,sans-serif;font-size:14px;color:#1a1a1a;">
<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="background:#f4f4f4;">
<tr><td align="center" style="padding:20px 10px;">
<table role="presentation" width="950" cellpadding="0" cellspacing="0" border="0" align="center" style="margin:0 auto;background:#ffffff;border:1px solid #cccccc;max-width:950px;width:950px;">
<tr><td align="center" style="background:${BRAND_GREEN};padding:26px;text-align:center;"><img src="${LOGO_URL}" width="280" alt="Benchmark Insurance Brokers" style="display:block;margin:0 auto;border:0;outline:none;"/></td></tr>
<tr><td style="padding:32px 42px;">
<p style="margin:0 0 16px 0;">Dear <strong>${coverData.contact_name||coverData.insured_name||'Client'}</strong>,</p>
<p style="margin:0 0 16px 0;">${introText}</p>

<p style="margin:0 0 10px 0;font-weight:700;">Policy Details</p>
<table role="presentation" width="100%" cellpadding="8" cellspacing="0" border="1" style="border-collapse:collapse;border-color:#cccccc;margin-bottom:18px;">
${policyTableRows}
</table>

${endorsementDetailsHtml}

<p style="margin:0 0 8px 0;font-weight:700;">Documents enclosed for your attention:</p><ul style="margin:0 0 20px 0;padding-left:22px;font-size:13px;line-height:1.9;">${allDocsHtml}</ul>
${(()=>{ const ct=(coverData.cover_type_display||coverData.cover_type||'').toLowerCase(); if(ct.includes('truck')||ct.includes('transport')||ct.includes('commercial motor')||ct.includes('plant')) return `<p style="margin:0 0 6px 0;font-size:14px;font-weight:700;color:#1a1a1a;">Important Notices — ${coverData.cover_type_display||coverData.cover_type}</p><table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="margin:0 0 20px 0;"><tr><td style="background:#f8f9fa;border:1px solid #dee2e6;border-radius:8px;padding:16px 18px;"><table width="100%" cellpadding="0" cellspacing="0" border="0">${[['Prior Insurance','Insurers usually provide a discount if you have advised us that previously you had continuous insurance. It is extremely important you have proof of this on file. In the event of a claim your insurer may ask for previous proof of insurance. If you cannot produce proof, your insurer may re-rate your policy to a much higher premium and put you in financial stress at claim time. Please call us if you need to discuss this.'],['Additional Excesses','There are likely additional excesses for certain events in claims. Please ensure you check over your schedule and PDS to familiarise yourself with any additional excesses that may apply.'],['Radius of Travel','Contact us immediately if you will be exceeding your radius of travel as listed in your Schedule of Insurance.'],['Claims History','It is very important your insurer is aware of your full claims history as this forms part of the basis on which cover is offered. If you need to disclose any previous claims, please contact us immediately.'],['Sum Insured','If your items are insured on a sum insured basis, your insurer will pay the lesser of market value or the sum insured stated on your schedule.'],['Downtime Cover','Unless you have specifically opted for downtime cover (it will be noted on your schedule), there is no cover for loss of income in the event of a claim.']].map(([heading,body])=>`<tr><td style="padding:8px 0;border-bottom:1px solid #e9ecef;vertical-align:top;"><p style="margin:0 0 3px 0;font-size:13px;font-weight:700;color:#1e3d30;">${heading}</p><p style="margin:0;font-size:12.5px;line-height:1.7;color:#1a1a1a;">${body}</p></td></tr>`).join('')}</table></td></tr></table>`; return ''; })()}
${coverData.key_exclusions?`<p style="margin:0 0 6px 0;font-weight:700;">Key Exclusions / Conditions to Note</p><p style="margin:0 0 20px 0;font-size:13px;line-height:1.7;">${coverData.key_exclusions.replace(/\n/g,'<br/>')}</p>`:''}
${coverData.additional_notes?`<p style="margin:0 0 6px 0;font-weight:700;">Additional Notes</p><p style="margin:0 0 20px 0;font-size:13px;line-height:1.7;">${coverData.additional_notes.replace(/\n/g,'<br/>')}</p>`:''}
<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="margin:20px 0;">
<tr><td style="background:${BRAND_GREEN};padding:20px 26px;">
<p style="margin:0;font-size:11px;font-weight:700;letter-spacing:1px;text-transform:uppercase;color:rgba(255,255,255,0.6);">Benchmark Insurance Brokers</p>
<p style="margin:6px 0 8px 0;font-size:20px;font-weight:800;color:#ffffff;">Because Standards Matter.</p>
<p style="margin:0;font-size:12px;color:rgba(255,255,255,0.75);">Personalised service. Practical advice. Strong claims advocacy.</p>
</td></tr></table>
${paymentSection}
${buildDutyHtml(coverData.cover_type||'')}
<p style="margin:0 0 6px 0;font-size:14px;font-weight:700;color:#1a1a1a;">Making a Claim</p>
<p style="margin:0 0 20px 0;font-size:13px;line-height:1.7;color:#1a1a1a;">In the event of a claim or any circumstance that may give rise to a claim, contact us immediately at ${BM.phone} or <a href="mailto:${BM.email}" style="color:#1e3d30;">${BM.email}</a>. <strong>Do not admit liability to any third party.</strong></p>
<p style="margin:0 0 20px 0;font-size:13px;line-height:1.7;color:#1a1a1a;">Please retain these documents for your records. If you have any questions, reply to this email or call us on <a href="tel:${BM.phone}" style="color:#1e3d30;">${BM.phone}</a>.</p>
<p style="margin:0;">Yours faithfully,<br/><br/>${brokerSignoff}</p>
</td></tr>
<tr><td style="background:#f0f4f2;border-top:1px solid #d7e5de;padding:18px 42px;">
<p style="margin:0 0 6px 0;font-size:11px;color:#5f746c;line-height:1.7;"><strong>${BM.name}</strong> trading as <strong>${BM.trading}</strong> | ABN ${BM.abn} | AR No. ${BM.ar}<br/>Authorised Representative of <strong>${BM.licensee}</strong> | ABN ${BM.lic_abn} | AFSL ${BM.lic_afsl}</p>
<p style="margin:0;font-size:11px;line-height:1.8;">${footerLinks}</p>
</td></tr>
</table>
</td></tr></table>
</body>
</html>`
}

// ── DocUploadSlot ─────────────────────────────────────────────────────────────

function DocUploadSlot({ docKey, label, checked, fileName, uploading, onToggle, onFileDrop, onRemove }) {
  const [dragOver, setDragOver] = useState(false)
  const inputRef = useRef(null)
  const handleDrop = (e) => { e.preventDefault(); setDragOver(false); const f=e.dataTransfer.files?.[0]; if(f) onFileDrop(docKey,f) }
  const handleInput = (e) => { const f=e.target.files?.[0]; if(f) onFileDrop(docKey,f); e.target.value='' }
  return (
    <div style={{ border:`1.5px solid ${checked?'var(--forest)':'var(--border)'}`, borderRadius:10, background:checked?'var(--forest-subtle)':'var(--surface)', overflow:'hidden' }}>
      <div style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 12px' }}>
        <div onClick={()=>onToggle(docKey)} style={{ width:18, height:18, borderRadius:4, border:`2px solid ${checked?'var(--forest)':'var(--border)'}`, background:checked?'var(--forest)':'#fff', color:'#fff', display:'flex', alignItems:'center', justifyContent:'center', fontSize:11, flexShrink:0, cursor:'pointer' }}>{checked?'✓':''}</div>
        <div style={{ fontSize:12, fontWeight:600, color:checked?'var(--forest)':'var(--muted)', flex:1 }}>{label}</div>
        {fileName && <button onClick={()=>onRemove(docKey)} style={{ background:'none', border:'none', color:'var(--muted)', cursor:'pointer', fontSize:14 }}>✕</button>}
      </div>
      {(docKey==='benchmark_info'||docKey==='fsg') ? (
        <div style={{ margin:'0 10px 10px', padding:'7px 10px', borderRadius:7, border:'1.5px dashed var(--forest-border)', display:'flex', alignItems:'center', gap:8 }}>
          <span style={{ fontSize:11, color:'var(--forest)', fontWeight:600 }}>Sent as link — check box to include</span>
          <a href={docKey==='fsg'?FSG_URL:IMPORTANT_INFO_URL} target="_blank" rel="noreferrer" style={{ fontSize:11, color:'var(--forest)', marginLeft:'auto' }}>Preview ↗</a>
        </div>
      ) : (
        <div onDragOver={e=>{e.preventDefault();setDragOver(true)}} onDragLeave={()=>setDragOver(false)} onDrop={handleDrop} onClick={()=>inputRef.current?.click()}
          style={{ margin:'0 10px 10px', padding:'7px 10px', borderRadius:7, border:`1.5px dashed ${dragOver?'var(--forest)':fileName?'var(--forest)':'rgba(107,130,120,0.4)'}`, background:dragOver?'rgba(30,61,48,0.08)':'transparent', cursor:'pointer', display:'flex', alignItems:'center', gap:8, minHeight:34 }}>
          {uploading ? <span style={{ fontSize:11, color:'var(--muted)' }}>Uploading...</span>
            : fileName ? <span style={{ fontSize:11, color:'var(--forest)', fontWeight:600 }}>{fileName}</span>
            : <span style={{ fontSize:11, color:'var(--muted)' }}>Drop file or click to upload</span>}
          <input ref={inputRef} type="file" style={{ display:'none' }} onChange={handleInput} />
        </div>
      )}
    </div>
  )
}

// ── AI polish ─────────────────────────────────────────────────────────────────

async function polishBindNotes(coverData, brokerName, isEndorsement, session) {
  const endorsementContext = isEndorsement ? `
Endorsement Type: ${coverData.endorsement_type || 'Not specified'}
Endorsement Description: ${coverData.endorsement_description || 'Not specified'}
Endorsement Reason: ${coverData.endorsement_reason || 'Not specified'}
Premium Adjustment: ${coverData.adjustment_type || 'Not specified'}` : ''

  const prompt = `You are a compliance assistant for Benchmark Insurance Brokers. Rewrite each section to be clear, concise and professional — suitable for an internal compliance audit file. Use third-person professional language. Do not add information not in the original.

Broker: ${brokerName}
Cover Type: ${coverData.cover_type||'Not specified'}
Insured: ${coverData.insured_name||'Not specified'}
Insurer: ${coverData.insurer||'Not specified'}
${isEndorsement ? `Transaction Type: Policy Endorsement${endorsementContext}` : 'Transaction Type: New Policy / Renewal'}

CLIENT & SITUATION SUMMARY (raw): ${coverData.bind_client_summary||'Not provided'}
WHY TMD WAS SUITABLE (raw): ${coverData.bind_tmd_reason||'Not provided'}
WHY THIS INSURER WAS CHOSEN (raw): ${coverData.bind_insurer_reason||'Not provided'}
PAYMENT NOTES (raw): ${coverData.bind_payment_notes||'Not provided'}

Respond ONLY with a valid JSON object, no markdown, no preamble:
{"bind_client_summary":"...","bind_tmd_reason":"...","bind_insurer_reason":"...","bind_payment_notes":"..."}`

const response = await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/polish-bind-notes`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
    body: JSON.stringify({ prompt }),
  })
  const data = await response.json()
  const text = (data.text || data.content?.map(b=>b.text||'').join('') || '').trim()
  return JSON.parse(text.replace(/```json|```/g,'').trim())
}

// ── Main modal ────────────────────────────────────────────────────────────────

export function CoverConfirmedModal({ client, user, onClose, showToast, loadAll }) {
  const brokerName  = user?.name || 'Your Broker'
  const brokerPhone = BROKER_PHONES[brokerName] || BM.phone
  const brokerEmail = BROKER_EMAILS[brokerName] || BM.email
  const brokerTitle = BROKER_TITLES[brokerName] || 'Broker'

  const defaultDocs = DOC_TYPES.reduce((acc,d)=>({...acc,[d.key]:false}),{})

  const [recordId] = useState(() => crypto.randomUUID())
  const [saving, setSaving]             = useState(false)
  const [factFinds, setFactFinds]       = useState([])
  const [selectedFfId, setSelectedFfId] = useState('')

  useEffect(() => {
    const loadFactFinds = async () => {
      const { data } = await supabase
        .from('clients')
        .select('fact_finds')
        .eq('id', client.id)
        .single()
      if (data?.fact_finds?.length) setFactFinds(data.fact_finds)
    }
    loadFactFinds()
  }, [client.id])
const [showSendChecklist, setShowSendChecklist] = useState(false)
  const [sending, setSending]           = useState(false)
  const [sendError, setSendError]       = useState('')
  const [polishing, setPolishing]       = useState(false)
  const [uploadingDoc, setUploadingDoc] = useState({})
  const [bindOpen, setBindOpen]         = useState(false)
  const [isEndorsement, setIsEndorsement] = useState(false)
  const [sendReviewSms, setSendReviewSms] = useState(true)
  const [scriptOpen, setScriptOpen]     = useState(false)
  const [scriptPayment, setScriptPayment] = useState('annual')

const DRAFT_KEY = `cc_draft_${client.id}_${user?.id||'unknown'}`
  const savedDraft = (() => { try { const s=localStorage.getItem(DRAFT_KEY); return s?JSON.parse(s):null } catch{return null} })()

  const [coverData, setCoverData]       = useState(savedDraft || {
insured_name:        client.company_name||'',
    contact_name:        client.contact_name||'',
    insurer:             '',
    tmd_confirmed:       false,
    policy_number:       '',
    cover_type:          client.policy_type||'',
    period_from:         '',
    period_to:           '',
    payment_type:        'annual',
    payment_frequency:   'monthly',
    premium_funder:      '',
    total_invoice:       '',
    payment_due_date:    '',
    docs_attached:       defaultDocs,
    doc_files:           {},
    key_exclusions:      '',
    additional_notes:    '',
    bind_client_summary: '',
    bind_tmd_reason:     '',
    bind_insurer_reason: '',
    bind_payment_notes:  '',
    // Endorsement fields
    endorsement_type:           '',
    endorsement_description:    '',
    endorsement_reason:         '',
    endorsement_effective_date: '',
    adjustment_type:            '',
  })

  const workingCompliance = useMemo(
    () => buildComplianceState(client, { policy_type: coverData.cover_type||client.policy_type||'' }),
    [client, coverData.cover_type]
  )

  useEffect(() => {
    if (!coverData.period_from) return
    setCoverData(prev => ({
      ...prev,
      period_to:        addOneYear(prev.period_from),
      payment_due_date: prev.payment_type==='annual' ? prev.period_from : prev.payment_due_date,
    }))
  }, [coverData.period_from])

  const update = (key, val) => setCoverData(prev=>{ const next={...prev,[key]:val}; try{localStorage.setItem(DRAFT_KEY,JSON.stringify(next))}catch{}; return next })

  const clearDraft = () => { try{localStorage.removeItem(DRAFT_KEY)}catch{} }

  const anyDocAttached = DOC_TYPES.some(d=>coverData.docs_attached?.[d.key])
  const attachedCount  = DOC_TYPES.filter(d=>coverData.docs_attached?.[d.key]).length
  const retail         = isRetailType(coverData.cover_type||'')
  const claimsMade     = isClaimsMadeType(coverData.cover_type||'')

  const htmlPreview = useMemo(
    () => buildCoverConfirmedHtml({ coverData, brokerName, brokerPhone, brokerTitle, isEndorsement }),
    [coverData, brokerName, brokerPhone, brokerTitle, isEndorsement]
  )

  const handleFileDrop = async (docKey, file) => {
    setUploadingDoc(prev=>({...prev,[docKey]:true}))
    try {
      const ext  = file.name.split('.').pop()
      const path = `cover-confirmations/${client.id}/${docKey}-${Date.now()}.${ext}`
      const { error } = await supabase.storage.from('documents').upload(path, file, { upsert:true })
      if (error) throw error
      const { data } = supabase.storage.from('documents').getPublicUrl(path)
      setCoverData(prev=>({
        ...prev,
        docs_attached: {...prev.docs_attached,[docKey]:true},
        doc_files:     {...prev.doc_files,    [docKey]:{ name:file.name, path, url:data.publicUrl }},
      }))
      showToast(`Attached ${file.name}`)
    } catch(e) { showToast(`Upload failed: ${e.message}`) }
    setUploadingDoc(prev=>({...prev,[docKey]:false}))
  }

  const handleFileRemove = async (docKey) => {
    const existing = coverData.doc_files?.[docKey]
    if (existing?.path) await supabase.storage.from('documents').remove([existing.path])
    setCoverData(prev => {
      const files = {...prev.doc_files}
      delete files[docKey]
      return { ...prev, doc_files:files, docs_attached:{...prev.docs_attached,[docKey]:false} }
    })
  }

  const handleToggle = (docKey) =>
    setCoverData(prev=>({...prev, docs_attached:{...prev.docs_attached,[docKey]:!prev.docs_attached?.[docKey]}}))

  const handlePolish = async () => {
    const hasAny = coverData.bind_client_summary||coverData.bind_tmd_reason||coverData.bind_insurer_reason||coverData.bind_payment_notes
    if (!hasAny) return showToast('Add some bind notes first before polishing')
    setPolishing(true)
try {
      const { data:{ session } } = await supabase.auth.getSession()
      const polished = await polishBindNotes(coverData, brokerName, isEndorsement, session)
      setCoverData(prev=>({...prev,...polished}))
      showToast('✨ Bind notes polished')
    } catch(e) { showToast('AI polish failed — try again') }
    setPolishing(false)
  }

  const openPdf = () => {
    const w = window.open('','_blank','width=1200,height=900')
    if (!w) return
    const title = isEndorsement ? 'Endorsement Confirmation' : 'Cover Confirmed'
    w.document.write(`<html><head><title>${title}</title><style>*{-webkit-print-color-adjust:exact!important;print-color-adjust:exact!important}body{margin:0;background:#f4f4f4}.bar{position:fixed;top:16px;right:16px;z-index:9999;display:flex;gap:8px}.btn{background:#1e3d30;color:#fff;border:none;padding:10px 20px;border-radius:10px;font-size:14px;font-weight:700;cursor:pointer}.cls{background:#6b8278}@media print{.bar{display:none!important}@page{margin:0;size:A4}}</style></head><body><div class="bar"><button class="btn" onclick="window.print()">Print / Save PDF</button><button class="btn cls" onclick="window.close()">Close</button></div>${htmlPreview}</body></html>`)
    w.document.close(); w.focus()
  }

  const recordTitle = isEndorsement
    ? `Endorsement — ${coverData.endorsement_type||coverData.cover_type||'Policy'} — ${coverData.insured_name}`
    : `${coverData.cover_type||'Cover'} — ${coverData.insured_name}`

  const saveRecord = async () => {
    if (!anyDocAttached) return showToast('Attach at least one document before saving')
    if (isEndorsement && !coverData.endorsement_description) return showToast('Describe what the endorsement changes before saving')
    setSaving(true)
    try {
const selectedFf = factFinds.find(ff => ff.id === selectedFfId) || null
// Auto-log to Commissions pending
try {
  const _cd = coverData || formData || {}
  const _isEnd = ((_cd.tran_type || _cd.transaction_type || _cd.type || '').toLowerCase().includes('endorse')) ||
                 ((selectedTab || activeTab || mode || '').toLowerCase().includes('endorse'))
  await supabase.from('manual_sales').insert({
    client_name: _cd.insured_name || _cd.client_name || clientName || '',
    broker: user?.name || userName || '',
    cover_type: _cd.cover_type || '',
    insurer: _cd.insurer || '',
    policy_number: _cd.policy_number || '',
    base_premium: parseFloat((_cd.base_premium || '').toString().replace(/[^\d.]/g,'')) || 0,
    comm_rate: parseFloat(_cd.comm_rate || 0) || 0,
    broker_fee: parseFloat((_cd.broker_fee || '').toString().replace(/[^\d.]/g,'')) || 0,
    est_income: (parseFloat((_cd.broker_fee || '').toString().replace(/[^\d.]/g,'')) || 0) * 0.83 +
                ((parseFloat((_cd.base_premium || '').toString().replace(/[^\d.]/g,'')) || 0) *
                (parseFloat(_cd.comm_rate || 0) / 100)) * 0.93,
    sold_date: new Date().toISOString().slice(0,10),
    tran_type: _isEnd ? 'Endorsement' : 'New Business',
  })
} catch(_e) { console.warn('Commissions log failed silently:', _e.message) }
      const { error } = await supabase.from('cover_confirmations').upsert({
        id:                 recordId,
        client_id:          client.id,
        created_by:         brokerName,
        title:              recordTitle,
        status:             'Draft',
        cover_data:         {...coverData, is_endorsement:isEndorsement, compliance_snapshot:workingCompliance},
        html_preview:       htmlPreview,
        fact_find_id:       selectedFfId || null,
        fact_find_snapshot: selectedFf,
      })
      if (error) throw error
      if (loadAll) await loadAll()
showToast(isEndorsement ? 'Endorsement confirmation saved' : 'Cover confirmed saved')
      clearDraft(); onClose()
    } catch(e) { showToast(`Error saving: ${e.message}`) }
    setSaving(false)
  }

const sendEmail = async () => {
    if (!client.email)   return showToast('No email address on file for this client')
    if (!anyDocAttached) return showToast('Attach at least one document before sending')
    if (isEndorsement && !coverData.endorsement_description) return showToast('Describe what the endorsement changes before sending')
    setSendError('')
    setSending(true)
    try {
      const { data:{ session } } = await supabase.auth.getSession()
      const subject = isEndorsement
        ? `Endorsement Confirmation — ${coverData.insured_name||client.company_name} — ${coverData.cover_type||'Insurance'}${coverData.policy_number?` — ${coverData.policy_number}`:''}`
        : `Cover Confirmed — ${coverData.insured_name||client.company_name} — ${coverData.cover_type||'Insurance'}${coverData.policy_number?` — ${coverData.policy_number}`:''}`
      const docLabelMap = DOC_TYPES.reduce((acc, d) => ({ ...acc, [d.key]: d.label }), {})
      const attachmentFiles = Object.entries(coverData.doc_files||{}).map(([key, f]) => ({ name: f.name, path: f.path, label: docLabelMap[key] || f.name }))

      const response = await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/send-cover-confirmed`, {
        method:  'POST',
        headers: { Authorization:`Bearer ${session.access_token}`, 'Content-Type':'application/json' },
        body:    JSON.stringify({
          coverData:      {...coverData, is_endorsement:isEndorsement, compliance_snapshot:workingCompliance},
          brokerName,
          brokerEmail,
          clientEmail:    client.email,
          clientPhone:    sendReviewSms ? (client.phone || null) : null,
          clientName:     client.company_name || null,
clientId:       client.id || null,
          subject,
          attachmentFiles,
          factFindSnapshot: factFinds.find(ff => ff.id === selectedFfId) || null,
        }),
      })
      const result = await response.json()
      if (!response.ok||!result.success) throw new Error(result.error||'Send failed')

const selectedFf = factFinds.find(ff => ff.id === selectedFfId) || null
      await supabase.from('cover_confirmations').upsert({
        id:                 recordId,
        client_id:          client.id,
        created_by:         brokerName,
        title:              recordTitle,
        status:             'Sent',
        cover_data:         {...coverData, is_endorsement:isEndorsement, compliance_snapshot:workingCompliance},
        html_preview:       htmlPreview,
        fact_find_id:       selectedFfId || null,
        fact_find_snapshot: selectedFf,
      })

      await logClientEmailAction({
        supabase, client, userName:brokerName,
        emailType:       isEndorsement ? 'Endorsement Confirmation' : 'Cover Confirmed',
        subject,
        recipient:       client.email,
        snapshotUpdates: { last_cover_confirmed_sent_at:new Date().toISOString() },
        activityText:    `${isEndorsement ? 'Endorsement confirmation' : 'Cover confirmed'} sent by ${brokerName}`,
      })

      if (loadAll) await loadAll()
showToast(`✅ ${isEndorsement ? 'Endorsement confirmation' : 'Cover confirmed'} sent${sendReviewSms && client.phone ? ' + review SMS sent' : ''}`)
      clearDraft(); onClose()
} catch(e) {
      setSendError(e.message || 'Send failed')
      showToast(`Send failed — ${e.message}`)
    }
    setSending(false)
  }

  return (
    <div style={{ minHeight:'100vh', display:'flex', flexDirection:'column' }} onClick={e => e.stopPropagation()}>
      <div style={{ maxWidth:1100, width:'96vw', margin:'0 auto', flex:1, background:'var(--surface)' }}>

        <div className="modal-head">
          <div>
            <div className="modal-title">{isEndorsement ? '✏️ Endorsement Confirmation' : 'Cover Confirmed Email'}</div>
            <div style={{ fontSize:12, color:'var(--muted)', marginTop:3, display:'flex', gap:6, flexWrap:'wrap', alignItems:'center' }}>
              <span>{client.company_name}</span>
              {coverData.cover_type && <span>· {coverData.cover_type}</span>}
              {isEndorsement && coverData.endorsement_type && <span>· {coverData.endorsement_type}</span>}
              <span style={{ padding:'1px 8px', borderRadius:10, background:retail?'rgba(30,61,48,0.1)':'rgba(59,130,246,0.1)', color:retail?'var(--forest)':'#2563eb', fontWeight:600, fontSize:11 }}>
                {retail ? '✓ Retail — Misrep + TMD + Informed Consent' : claimsMade ? '✓ Claims-Made — DoD + Claims Notice' : '✓ Wholesale — Duty of Disclosure'}
              </span>
            </div>
          </div>
          {!isEndorsement && (
            <button
              onClick={() => setScriptOpen(true)}
              style={{ background:'#e6a817', color:'#000', border:'none', borderRadius:8, padding:'6px 14px', fontSize:12, fontWeight:700, cursor:'pointer', fontFamily:'var(--font)', marginRight:8 }}
            >
              📋 Closing Script
            </button>
          )}
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>

        <div className="modal-body" style={{ overflowY:'auto', maxHeight:'calc(94vh - 120px)' }}>

          {/* ── Mode toggle ── */}
          <div style={{ display:'flex', gap:0, marginBottom:18, borderRadius:10, overflow:'hidden', border:'2px solid var(--border)' }}>
            <button onClick={()=>setIsEndorsement(false)} style={{ flex:1, padding:'10px 0', border:'none', background:!isEndorsement?'var(--forest)':'var(--surface2)', color:!isEndorsement?'#fff':'var(--muted)', fontWeight:700, fontSize:13, cursor:'pointer', fontFamily:'var(--font)', transition:'all .15s' }}>
              New Policy / Renewal
            </button>
            <button onClick={()=>setIsEndorsement(true)} style={{ flex:1, padding:'10px 0', border:'none', borderLeft:'2px solid var(--border)', background:isEndorsement?'var(--forest)':'var(--surface2)', color:isEndorsement?'#fff':'var(--muted)', fontWeight:700, fontSize:13, cursor:'pointer', fontFamily:'var(--font)', transition:'all .15s' }}>
              ✏️ Endorsement
            </button>
          </div>

{/* ── Core policy fields ── */}
          <div className="form-grid" style={{ marginBottom:20 }}>
            <div><label className="form-label">Insured Name <span style={{fontWeight:400,color:'var(--muted)'}}>— legal entity on the policy</span></label><input className="form-input" value={coverData.insured_name} onChange={e=>update('insured_name',e.target.value)} /></div>
            <div><label className="form-label">Attention / Contact <span style={{fontWeight:400,color:'var(--muted)'}}>— who the email greets</span></label><input className="form-input" value={coverData.contact_name||''} onChange={e=>update('contact_name',e.target.value)} placeholder="e.g. Jen Grimsey" /></div>
            <div>
              <label className="form-label">Insurer</label>
              <select
                className="form-select"
                value={INSURER_LIST.includes(coverData.insurer) ? coverData.insurer : (coverData.insurer ? 'Other' : '')}
                onChange={e => {
                  if (e.target.value === 'Other') update('insurer', '')
                  else update('insurer', e.target.value)
                }}
              >
                <option value="">Select insurer...</option>
                {INSURER_LIST.map(i => <option key={i} value={i}>{i}</option>)}
              </select>
              {(!INSURER_LIST.includes(coverData.insurer) || coverData.insurer === '') && (
                <input
                  className="form-input"
                  style={{ marginTop:6 }}
                  value={coverData.insurer}
                  onChange={e => update('insurer', e.target.value)}
                  placeholder="Type insurer name if not listed above..."
                />
              )}
            </div>
            <div><label className="form-label">Policy Number</label><input className="form-input" value={coverData.policy_number} onChange={e=>update('policy_number',e.target.value)} /></div>
            <div>
              <label className="form-label">Cover Type</label>
              <select className="form-select" value={coverData.cover_type} onChange={e=>update('cover_type',e.target.value)}>
                <option value="">Select...</option>
                {CC_COVER_TYPES.map(t=><option key={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="form-label">Cover Type Display Name <span style={{fontWeight:400,color:'var(--muted)'}}>— overrides what appears in the email</span></label>
              <input className="form-input" value={coverData.cover_type_display||''} onChange={e=>update('cover_type_display',e.target.value)} placeholder={coverData.cover_type||'e.g. Transport Pack, Farm Combined'} />
            </div>
            <div><label className="form-label">Period From</label><input className="form-input" type="date" value={coverData.period_from} onChange={e=>update('period_from',e.target.value)} /></div>
            <div><label className="form-label">Period To</label><input className="form-input" type="date" value={coverData.period_to} onChange={e=>update('period_to',e.target.value)} /></div>
          </div>



          {/* ── ENDORSEMENT DETAILS BLOCK ── */}
          {isEndorsement && (
            <div style={{ border:'2px solid var(--forest-border)', borderRadius:12, padding:'18px 20px', marginBottom:20, background:'rgba(30,61,48,0.03)' }}>
              <div style={{ fontSize:14, fontWeight:700, color:'var(--forest)', marginBottom:14, display:'flex', alignItems:'center', gap:8 }}>
                ✏️ Endorsement Details
                <span style={{ fontSize:11, fontWeight:500, color:'var(--muted)' }}>— this appears in the client email and audit file</span>
              </div>
              <div className="form-grid">
                <div>
                  <label className="form-label">Endorsement Type</label>
                  <select className="form-select" value={coverData.endorsement_type} onChange={e=>update('endorsement_type',e.target.value)}>
                    <option value="">Select...</option>
                    {ENDORSEMENT_TYPES.map(t=><option key={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <label className="form-label">Effective Date of Endorsement</label>
                  <input className="form-input" type="date" value={coverData.endorsement_effective_date} onChange={e=>update('endorsement_effective_date',e.target.value)} />
                </div>
                <div className="full">
                  <label className="form-label" style={{ color:'var(--forest)', fontWeight:700 }}>What is being changed? *</label>
                  <textarea className="form-input" rows={3} value={coverData.endorsement_description} onChange={e=>update('endorsement_description',e.target.value)}
                    placeholder="Describe the exact change in plain language, e.g. 'Change building construction from brick veneer to weatherboard at 14 Smith St, Toowong' or 'Add 2024 Toyota HiLux Rego ABC123 to fleet schedule'"
                    style={{ borderColor: coverData.endorsement_description ? 'var(--forest)' : undefined }}
                  />
                  {!coverData.endorsement_description && (
                    <div style={{ fontSize:11, color:'var(--danger)', marginTop:4, fontWeight:600 }}>Required — clearly state what is being endorsed</div>
                  )}
                </div>
                <div className="full">
                  <label className="form-label">Reason for Endorsement</label>
                  <textarea className="form-input" rows={2} value={coverData.endorsement_reason} onChange={e=>update('endorsement_reason',e.target.value)}
                    placeholder="e.g. Client corrected building details after reviewing policy schedule, client purchased new vehicle, refinanced with new lender"
                  />
                </div>
                <div>
                  <label className="form-label">Premium Adjustment</label>
                  <select className="form-select" value={coverData.adjustment_type} onChange={e=>update('adjustment_type',e.target.value)}>
                    <option value="">Select...</option>
                    <option value="Additional premium">Additional premium</option>
                    <option value="Refund due">Refund due</option>
                    <option value="No change expected">No change expected</option>
                    <option value="To be confirmed">To be confirmed</option>
                  </select>
                </div>
                {(coverData.adjustment_type === 'Additional premium' || coverData.adjustment_type === 'Refund due') && (
                  <div>
                    <label className="form-label">{coverData.adjustment_type === 'Additional premium' ? 'Additional Premium Amount' : 'Refund Amount'}</label>
                    <input className="form-input" value={coverData.total_invoice} onChange={e=>update('total_invoice',parseCurr(e.target.value))} onBlur={e=>update('total_invoice',formatCurr(e.target.value))} onFocus={e=>update('total_invoice',parseCurr(e.target.value))} placeholder="e.g. 250.00" />
                  </div>
                )}
                {coverData.adjustment_type === 'Additional premium' && (
                  <>
                    <div>
                      <label className="form-label">Payment Method</label>
                      <select className="form-select" value={coverData.payment_type} onChange={e=>update('payment_type',e.target.value)}>
                        <option value="annual">Annual / One-off payment</option>
                        <option value="monthly">Added to Premium Funding</option>
                      </select>
                    </div>
                    {coverData.payment_type === 'monthly' && (
                      <>
                        <div>
                          <label className="form-label">Premium Funder</label>
                          <select className="form-select" value={coverData.premium_funder} onChange={e=>update('premium_funder',e.target.value)}>
                            <option value="">Select...</option>
                            <option>Attvest</option>
                            <option>Victory</option>
                          </select>
                        </div>
                        <div>
                          <label className="form-label">Payment Frequency</label>
                          <select className="form-select" value={coverData.payment_frequency} onChange={e=>update('payment_frequency',e.target.value)}>
                            <option value="monthly">Monthly</option>
                            <option value="fortnightly">Fortnightly</option>
                            <option value="weekly">Weekly</option>
                          </select>
                        </div>
                        <div className="full" style={{ fontSize:11, color:'var(--muted)', padding:'4px 0' }}>
                          11% loading applied: {getLoadedTotal(coverData.total_invoice) || '—'}
                          {getInstallment(coverData.total_invoice, FREQ_OPTIONS[coverData.payment_frequency]?.divisor)
                            ? ` total (${getInstallment(coverData.total_invoice, FREQ_OPTIONS[coverData.payment_frequency]?.divisor)}/${coverData.payment_frequency})`
                            : ''}
                        </div>
                      </>
                    )}
                  </>
                )}
              </div>
            </div>
          )}

{/* ── Payment fields (new policy / renewal only) ── */}
          {!isEndorsement && (
            <div style={{ marginBottom:20 }}>
              {/* Payment type chips */}
              <label className="form-label">Payment Type</label>
              <div style={{ display:'flex', gap:8, marginBottom:14 }}>
                {[{val:'annual',label:'💳 Annual'},{val:'monthly',label:'📅 Monthly (Premium Funding)'}].map(opt=>(
                  <button key={opt.val} onClick={()=>update('payment_type',opt.val)}
                    style={{ flex:1, padding:'10px 0', borderRadius:9, border:`2px solid ${coverData.payment_type===opt.val?'var(--forest)':'var(--border)'}`, background:coverData.payment_type===opt.val?'var(--forest)':'var(--surface2)', color:coverData.payment_type===opt.val?'#fff':'var(--muted)', fontWeight:700, fontSize:13, cursor:'pointer', fontFamily:'var(--font)', transition:'all .15s' }}>
                    {opt.label}
                  </button>
                ))}
              </div>

              {coverData.payment_type==='monthly' ? (
                <div className="form-grid">
                  {/* Funder chips */}
                  <div className="full">
                    <label className="form-label">Premium Funder</label>
                    <div style={{ display:'flex', gap:8 }}>
                      {['Attvest','Victory'].map(f=>(
                        <button key={f} onClick={()=>update('premium_funder',f)}
                          style={{ flex:1, padding:'9px 0', borderRadius:9, border:`2px solid ${coverData.premium_funder===f?'var(--forest)':'var(--border)'}`, background:coverData.premium_funder===f?'var(--forest)':'var(--surface2)', color:coverData.premium_funder===f?'#fff':'var(--muted)', fontWeight:700, fontSize:13, cursor:'pointer', fontFamily:'var(--font)', transition:'all .15s' }}>
                          {f}
                        </button>
                      ))}
                    </div>
                  </div>
                  {/* Frequency chips */}
                  <div className="full">
                    <label className="form-label">Payment Frequency</label>
                    <div style={{ display:'flex', gap:8 }}>
                      {[{val:'monthly',label:'Monthly'},{val:'fortnightly',label:'Fortnightly'},{val:'weekly',label:'Weekly'}].map(opt=>(
                        <button key={opt.val} onClick={()=>update('payment_frequency',opt.val)}
                          style={{ flex:1, padding:'9px 0', borderRadius:9, border:`2px solid ${coverData.payment_frequency===opt.val?'var(--forest)':'var(--border)'}`, background:coverData.payment_frequency===opt.val?'var(--forest)':'var(--surface2)', color:coverData.payment_frequency===opt.val?'#fff':'var(--muted)', fontWeight:700, fontSize:13, cursor:'pointer', fontFamily:'var(--font)', transition:'all .15s' }}>
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </div>
                  {/* Amount + live instalment calc */}
                  <div className="full">
                    <label className="form-label">Total Invoice Amount (before loading)</label>
                    <input className="form-input" value={coverData.total_invoice} onChange={e=>update('total_invoice',parseCurr(e.target.value))} onBlur={e=>update('total_invoice',formatCurr(e.target.value))} onFocus={e=>update('total_invoice',parseCurr(e.target.value))} placeholder="e.g. 1250.00" />
                    {coverData.total_invoice && Number(parseCurr(coverData.total_invoice)) > 0 && (
                      <div style={{ marginTop:8, display:'flex', gap:8, flexWrap:'wrap' }}>
                        <div style={{ flex:1, minWidth:160, padding:'8px 12px', borderRadius:8, background:'var(--forest-subtle)', border:'1.5px solid var(--forest-border)' }}>
                          <div style={{ fontSize:10, fontWeight:700, color:'var(--forest)', textTransform:'uppercase', letterSpacing:'.4px' }}>Funded Total (11% loading)</div>
                          <div style={{ fontSize:16, fontWeight:800, color:'var(--forest)', marginTop:2 }}>{getLoadedTotal(coverData.total_invoice)}</div>
                        </div>
                        <div style={{ flex:1, minWidth:160, padding:'8px 12px', borderRadius:8, background:'var(--surface2)', border:'1.5px solid var(--border)' }}>
                          <div style={{ fontSize:10, fontWeight:700, color:'var(--muted)', textTransform:'uppercase', letterSpacing:'.4px' }}>Est. {FREQ_OPTIONS[coverData.payment_frequency]?.label || 'Monthly'} Instalment</div>
                          <div style={{ fontSize:16, fontWeight:800, color:'var(--text)', marginTop:2 }}>{getInstallment(coverData.total_invoice, FREQ_OPTIONS[coverData.payment_frequency]?.divisor) || '—'}</div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="form-grid">
                  <div>
                    <label className="form-label">Total Invoice Amount</label>
                    <input className="form-input" value={coverData.total_invoice} onChange={e=>update('total_invoice',parseCurr(e.target.value))} onBlur={e=>update('total_invoice',formatCurr(e.target.value))} onFocus={e=>update('total_invoice',parseCurr(e.target.value))} placeholder="e.g. 1250.00" />
                  </div>
                  <div>
                    <label className="form-label">Payment Due Date</label>
                    <input className="form-input" type="date" value={coverData.payment_due_date} onChange={e=>update('payment_due_date',e.target.value)} />
                  </div>
                </div>
              )}
            </div>
          )}
          {/* ── Fact Find Selector ── */}
          <div style={{ marginBottom:20 }}>
            <label className="form-label">Link a Fact Find <span style={{ fontWeight:400, color:'var(--muted)' }}>— snapshot saved with this record for compliance</span></label>
            {factFinds.length === 0
              ? <div style={{ fontSize:12, color:'var(--muted)', padding:'10px 14px', background:'var(--surface2)', borderRadius:9, border:'1.5px dashed var(--border)' }}>
                  No fact finds on file for this client — <span style={{ color:'var(--forest)', fontWeight:600 }}>complete a fact find first</span>
                </div>
              : <select className="form-select" value={selectedFfId} onChange={e => setSelectedFfId(e.target.value)}>
                  <option value="">Select a fact find...</option>
                  {factFinds.map(ff => (
                    <option key={ff.id} value={ff.id}>
                      {ff.name || ff.type || 'Fact Find'} — {ff.created_at ? new Date(ff.created_at).toLocaleDateString('en-AU') : ''}
                    </option>
                  ))}
                </select>
            }
            {selectedFfId && (
              <div style={{ marginTop:8, fontSize:11, color:'var(--forest)', fontWeight:600, padding:'6px 12px', background:'rgba(30,61,48,0.06)', borderRadius:7, border:'1.5px solid var(--forest-border)' }}>
                ✓ Fact find will be snapshotted into this record on save — answers locked at time of binding
              </div>
            )}
          </div>

          {/* ── Documents ── */}
          <div style={{ marginBottom:20 }}>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:10 }}>
              <div style={{ fontSize:13, fontWeight:600 }}>Documents Being Sent</div>
              <span style={{ fontSize:11, color:'var(--muted)' }}>{attachedCount} attached</span>
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
              {DOC_TYPES.map(d=>(
                <DocUploadSlot key={d.key} docKey={d.key} label={d.label}
                  checked={!!coverData.docs_attached?.[d.key]}
                  fileName={coverData.doc_files?.[d.key]?.name||null}
                  uploading={!!uploadingDoc[d.key]}
                  onToggle={handleToggle} onFileDrop={handleFileDrop} onRemove={handleFileRemove}
                />
              ))}
            </div>
            <div style={{ marginTop:8, fontSize:11, color:'var(--muted)' }}>Only checked documents appear in the client email.</div>
          </div>

          {/* ── Key exclusions & notes ── */}
          <div style={{ marginBottom:14 }}>
            <label className="form-label">Key Exclusions / Conditions to Note</label>
            <textarea className="form-input" rows={3} value={coverData.key_exclusions} onChange={e=>update('key_exclusions',e.target.value)} placeholder="Be specific — this appears verbatim in the client email" />
          </div>
          <div style={{ marginBottom:20 }}>
            <label className="form-label">Additional Notes</label>
            <textarea className="form-input" rows={3} value={coverData.additional_notes} onChange={e=>update('additional_notes',e.target.value)} />
          </div>

          {/* ── SMS preview ── */}
          <div style={{ marginBottom: 20 }}>
            {client.phone ? (
              <div style={{ padding:'12px 16px', background: sendReviewSms ? 'rgba(30,61,48,0.05)' : 'var(--surface2)', border:`1.5px solid ${sendReviewSms ? 'var(--forest-border)' : 'var(--border)'}`, borderRadius:12 }}>
                <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom: sendReviewSms ? 6 : 0 }}>
                  <div style={{ fontSize:12, fontWeight:700, color: sendReviewSms ? 'var(--forest)' : 'var(--muted)' }}>
                    📱 Google Review SMS
                  </div>
                  <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                    <span style={{ fontSize:11, color:'var(--muted)' }}>{sendReviewSms ? 'Will fire on send' : 'Disabled for this send'}</span>
                    <div
                      onClick={() => setSendReviewSms(s => !s)}
                      style={{ width:36, height:20, borderRadius:10, background: sendReviewSms ? 'var(--forest)' : 'var(--border)', cursor:'pointer', position:'relative', transition:'background .2s', flexShrink:0 }}
                    >
                      <div style={{ position:'absolute', top:2, left: sendReviewSms ? 18 : 2, width:16, height:16, borderRadius:'50%', background:'#fff', transition:'left .2s', boxShadow:'0 1px 3px rgba(0,0,0,0.2)' }} />
                    </div>
                  </div>
                </div>
                {sendReviewSms && (
                  <div style={{ fontSize:12, color:'var(--muted)', lineHeight:1.7 }}>
                    Hi {coverData.insured_name?.split(' ')[0]||client.company_name?.split(' ')[0]||'there'}, your {coverData.cover_type||'insurance'} {isEndorsement ? 'endorsement has been confirmed' : 'policy is now confirmed'} and your documents have been sent through to your email 🎉 If you found our service helpful, we'd love a quick Google review — it only takes 60 seconds: g.page/r/... — If there's anything you weren't happy with, just reply and I'll personally make it right. {brokerName.split(' ')[0]}, Benchmark Insurance Brokers
                  </div>
                )}
              </div>
            ) : (
              <div style={{ padding:'10px 14px', background:'rgba(220,53,69,0.06)', border:'1px solid rgba(220,53,69,0.2)', borderRadius:10, fontSize:12, color:'var(--danger)' }}>
                ⚠️ No phone number on file — Google Review SMS will not be sent
              </div>
            )}
          </div>

          {/* ── Bind notes ── */}
          <div style={{ border:'1.5px solid var(--border)', borderRadius:12, marginBottom:20, overflow:'hidden' }}>
            <button onClick={()=>setBindOpen(o=>!o)} style={{ width:'100%', display:'flex', alignItems:'center', justifyContent:'space-between', padding:'12px 18px', background:'var(--surface2)', border:'none', cursor:'pointer', fontFamily:'var(--font)' }}>
              <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                <span style={{ fontSize:13, fontWeight:600, color:'var(--forest)' }}>Internal Bind Notes</span>
                <span style={{ fontSize:11, color:'var(--muted)' }}>Not sent to client</span>
              </div>
              <span style={{ color:'var(--muted)', fontSize:14 }}>{bindOpen?'▲':'▼'}</span>
            </button>
            {bindOpen && (
              <div style={{ padding:'16px 18px' }}>
                <div style={{ display:'flex', justifyContent:'flex-end', marginBottom:14 }}>
                  <button onClick={handlePolish} disabled={polishing} style={{ fontSize:12, padding:'6px 16px', borderRadius:8, background:'var(--forest)', color:'#fff', border:'none', cursor:'pointer', fontFamily:'var(--font)', fontWeight:600, opacity: polishing ? 0.6 : 1 }}>
                    {polishing?'✨ Polishing...':'✨ Polish with AI'}
                  </button>
                </div>
<div style={{ display:'flex', flexDirection:'column', gap:12 }}>
                  <div><label className="form-label">Client & Situation Summary</label><textarea className="form-input" rows={3} value={coverData.bind_client_summary} onChange={e=>update('bind_client_summary',e.target.value)} placeholder={isEndorsement ? 'Brief summary of the client and what prompted this endorsement...' : 'Brief summary of the client, their business and the risk...'} /></div>

                  {/* TMD — checkbox attestation instead of free text */}
                  <div
                    onClick={()=>update('tmd_confirmed',!coverData.tmd_confirmed)}
                    style={{ display:'flex', alignItems:'flex-start', gap:12, padding:'12px 16px', borderRadius:10, border:`2px solid ${coverData.tmd_confirmed?'var(--forest)':'var(--border)'}`, background:coverData.tmd_confirmed?'var(--forest-subtle)':'var(--surface2)', cursor:'pointer', userSelect:'none' }}
                  >
                    <div style={{ width:20, height:20, borderRadius:5, border:`2px solid ${coverData.tmd_confirmed?'var(--forest)':'var(--border)'}`, background:coverData.tmd_confirmed?'var(--forest)':'#fff', color:'#fff', display:'flex', alignItems:'center', justifyContent:'center', fontSize:13, flexShrink:0, marginTop:1 }}>
                      {coverData.tmd_confirmed?'✓':''}
                    </div>
                    <div>
                      <div style={{ fontSize:13, fontWeight:700, color:coverData.tmd_confirmed?'var(--forest)':'var(--text)' }}>
                        TMD Confirmed — Product aligns with client's target market
                      </div>
                      <div style={{ fontSize:11, color:'var(--muted)', marginTop:3, lineHeight:1.5 }}>
                        I confirm that based on the information provided, this product aligns with the insurer's Target Market Determination and is appropriate for this client's likely objectives, financial situation and needs.
                      </div>
                    </div>
                  </div>

                  <div><label className="form-label">Why This Insurer Was Chosen</label><textarea className="form-input" rows={2} value={coverData.bind_insurer_reason} onChange={e=>update('bind_insurer_reason',e.target.value)} placeholder={isEndorsement ? 'Any notes on the insurer\'s handling of this endorsement...' : 'Reasons for recommending this insurer over alternatives...'} /></div>

                  {/* Payment summary — auto-populated from selections above, read-only */}
                  <div>
                    <label className="form-label">Payment Summary <span style={{ fontWeight:400, color:'var(--muted)' }}>— auto-filled from selections above</span></label>
                    <div style={{ padding:'10px 14px', borderRadius:9, background:'var(--surface2)', border:'1.5px solid var(--border)', fontSize:13, color:'var(--text)', lineHeight:1.7, minHeight:42 }}>
                      {isEndorsement
                        ? coverData.adjustment_type
                          ? <>
                              <strong>Adjustment:</strong> {coverData.adjustment_type}
                              {coverData.total_invoice ? ` — ${coverData.total_invoice}` : ''}
                              {coverData.adjustment_type === 'Additional premium' && coverData.payment_type === 'monthly' && coverData.premium_funder
                                ? ` via ${coverData.premium_funder} (${FREQ_OPTIONS[coverData.payment_frequency]?.label || 'Monthly'})`
                                : ''}
                            </>
                          : <span style={{ color:'var(--muted)' }}>No adjustment selected</span>
                        : coverData.payment_type === 'monthly'
                          ? <>
                              <strong>Monthly via {coverData.premium_funder || '—'}</strong>
                              {' · '}{FREQ_OPTIONS[coverData.payment_frequency]?.label || 'Monthly'} instalments
                              {coverData.total_invoice ? <> · Base premium {coverData.total_invoice} · Funded total {getLoadedTotal(coverData.total_invoice)} · Est. {getInstallment(coverData.total_invoice, FREQ_OPTIONS[coverData.payment_frequency]?.divisor)}/{coverData.payment_frequency}</> : ''}
                            </>
                          : <>
                              <strong>Annual</strong>
                              {coverData.total_invoice ? ` · ${coverData.total_invoice}` : ''}
                              {coverData.payment_due_date ? ` · Due ${fmtDate(coverData.payment_due_date)}` : ''}
                            </>
                      }
                    </div>
                  </div>

                  {isEndorsement && (
                    <div><label className="form-label">Additional Payment Notes</label><textarea className="form-input" rows={2} value={coverData.bind_payment_notes} onChange={e=>update('bind_payment_notes',e.target.value)} placeholder="Any additional payment details, refund processing notes..." /></div>
                  )}
                </div>
              </div>
            )}
          </div>

{/* ── Persistent send error banner ── */}
          {sendError && (
            <div style={{ marginBottom:14, padding:'14px 18px', background:'rgba(220,53,69,0.08)', border:'1.5px solid var(--danger)', borderRadius:12, display:'flex', alignItems:'flex-start', gap:12 }}>
              <span style={{ fontSize:20, flexShrink:0 }}>⚠️</span>
              <div style={{ flex:1 }}>
                <div style={{ fontSize:13, fontWeight:700, color:'var(--danger)', marginBottom:3 }}>Email was NOT sent</div>
                <div style={{ fontSize:12, color:'var(--text)', lineHeight:1.6 }}>{sendError}</div>
                <div style={{ fontSize:11, color:'var(--muted)', marginTop:6 }}>Fix the issue above, then send again. Nothing has gone to the client.</div>
              </div>
              <button onClick={()=>setSendError('')} style={{ background:'none', border:'none', color:'var(--muted)', cursor:'pointer', fontSize:16, flexShrink:0 }}>✕</button>
            </div>
          )}

          {/* ── Action buttons ── */}
          <div style={{ display:'flex', gap:8, marginBottom:20, flexWrap:'wrap', alignItems:'center' }}>
            <button className="btn btn-primary" onClick={saveRecord} disabled={saving||!anyDocAttached}>{saving?'Saving...':'Save Record'}</button>
            <button className="btn btn-primary" style={{ background:'#1e7a46' }} onClick={()=>{
  if (!client.email) return showToast('No email address on file for this client')
  if (!anyDocAttached) return showToast('Attach at least one document before sending')
  if (isEndorsement && !coverData.endorsement_description) return showToast('Describe what the endorsement changes before sending')
  setShowSendChecklist(true)
}} disabled={sending||!anyDocAttached||!client.email}>{sending?'Sending...':`✉ Send ${isEndorsement ? 'Endorsement' : ''} to Client`}</button>
            <button className="btn btn-secondary" onClick={openPdf}>Open PDF Preview</button>
            {!client.email && <span style={{ fontSize:11, color:'var(--danger)', fontWeight:600 }}>No email on file</span>}
          </div>

          {/* ── Closing script (new policy / renewal only) ── */}
          {scriptOpen && !isEndorsement && (
            <div className="overlay" onClick={e => e.target === e.currentTarget && setScriptOpen(false)}>
              <div className="modal" style={{ maxWidth: 640 }}>
                <div className="modal-head">
                  <div>
                    <div className="modal-title">📋 Closing Script</div>
                    <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 3 }}>Read aloud when client accepts — select payment type first</div>
                  </div>
                  <button className="modal-close" onClick={() => setScriptOpen(false)}>✕</button>
                </div>
                <div className="modal-body">
                  <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
                    <button
                      onClick={() => setScriptPayment('annual')}
                      style={{ flex: 1, padding: '10px 0', borderRadius: 8, border: `2px solid ${scriptPayment === 'annual' ? 'var(--forest)' : 'var(--border)'}`, background: scriptPayment === 'annual' ? 'var(--forest)' : 'var(--surface2)', color: scriptPayment === 'annual' ? '#fff' : 'var(--muted)', fontWeight: 600, fontSize: 13, cursor: 'pointer', fontFamily: 'var(--font)' }}
                    >
                      💳 Annual Payment
                    </button>
                    <button
                      onClick={() => setScriptPayment('monthly')}
                      style={{ flex: 1, padding: '10px 0', borderRadius: 8, border: `2px solid ${scriptPayment === 'monthly' ? 'var(--forest)' : 'var(--border)'}`, background: scriptPayment === 'monthly' ? 'var(--forest)' : 'var(--surface2)', color: scriptPayment === 'monthly' ? '#fff' : 'var(--muted)', fontWeight: 600, fontSize: 13, cursor: 'pointer', fontFamily: 'var(--font)' }}
                    >
                      📅 Monthly (Premium Funding)
                    </button>
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                    <div style={{ background: 'var(--forest-subtle)', border: '1.5px solid var(--forest-border)', borderRadius: 10, padding: '14px 16px' }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--forest)', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 8 }}>Opening</div>
<div style={{ fontSize: 14, lineHeight: 1.8, color: 'var(--text)' }}>
                        We're ready to arrange your cover. Subject to your confirmation, your cover will commence from <strong>{coverData.period_from ? new Date(coverData.period_from).toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' }) : '[start date]'}</strong>, and all policy documents will be emailed to you shortly.
                      </div>
                    </div>

                  <div style={{ background: 'var(--surface2)', border: '1.5px solid var(--border)', borderRadius: 10, padding: '14px 16px' }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 10 }}>Key Policy Summary</div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {[
                          ['Insurer', coverData.insurer || '[Insurer Name]'],
                          ['Cover Type', coverData.cover_type || '[Cover Type]'],
                          [scriptPayment === 'monthly' ? 'Funded Premium' : 'Total Invoice', scriptPayment === 'monthly' ? (getLoadedTotal(coverData.total_invoice) || '[Amount]') : (coverData.total_invoice || '[Amount]')],
                        ].map(([label, value]) => (
                          <div key={label} style={{ display: 'flex', gap: 12, fontSize: 13 }}>
                            <div style={{ width: 120, fontWeight: 600, color: 'var(--muted)', flexShrink: 0 }}>{label}</div>
                            <div style={{ color: 'var(--text)', fontWeight: 500 }}>{value}</div>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div style={{ background: 'var(--forest-subtle)', border: '1.5px solid var(--forest-border)', borderRadius: 10, padding: '14px 16px' }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--forest)', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 8 }}>Placement Consent</div>
                      <div style={{ fontSize: 14, lineHeight: 1.8, color: 'var(--text)' }}>
                        Before I arrange this, I just need to confirm: are you happy for us to place your cover with <strong>{coverData.insurer || '[Insurer Name]'}</strong>? … Thank you. I'll now arrange the cover and send your documents through.
                      </div>
                    </div>

                    <div style={{ background: 'var(--surface2)', border: '1.5px solid var(--border)', borderRadius: 10, padding: '14px 16px' }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 10 }}>Payment</div>
                      {scriptPayment === 'annual' ? (
                        <div style={{ fontSize: 14, lineHeight: 1.8 }}>
                          You can pay the premium in full via <strong>BPAY or card</strong> using the invoice we send through.
                        </div>
                      ) : (
                        <div style={{ fontSize: 14, lineHeight: 1.8 }}>
                          You've chosen to pay <strong>{coverData.payment_frequency || 'monthly'} via premium funding with {coverData.premium_funder || 'Attvest'}</strong>. Based on the annual premium of <strong>{coverData.total_invoice || '[Amount]'}</strong>, the estimated funded total is <strong>{getLoadedTotal(coverData.total_invoice) || '[Amount]'}</strong>, which includes an <strong>11% loading</strong>. That works out to approximately <strong>{getInstallment(coverData.total_invoice, FREQ_OPTIONS[coverData.payment_frequency]?.divisor) || '[Amount]'} {coverData.payment_frequency || 'monthly'}</strong>.<br/><br/>
                          If the policy is cancelled before expiry, you may still be required to pay out the remaining balance of the loan and any applicable interest, and any refund may be reduced. We recommend speaking with us before making any changes.
                        </div>
                      )}
                    </div>

                    <div style={{ background: 'var(--surface2)', border: '1.5px solid var(--border)', borderRadius: 10, padding: '14px 16px' }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 10 }}>Closing</div>
                      <div style={{ fontSize: 14, lineHeight: 1.8 }}>
                        Once you receive your documents, please review your <strong>record of answers</strong> to ensure all information is correct. You may also receive a <strong>DocuSign</strong> to complete your Record of Advice.<br/><br/>
                        We'll send everything through shortly — if you have any questions at all, please don't hesitate to reach out.
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ── Email preview ── */}
          <div style={{ fontSize:11, fontWeight:600, color:'var(--muted)', textTransform:'uppercase', letterSpacing:'.5px', marginBottom:8 }}>Email Preview</div>
      <div style={{ background:'#fbfcfb', border:'1.5px solid var(--border)', borderRadius:12, overflow:'auto', maxHeight:600 }} dangerouslySetInnerHTML={{ __html:htmlPreview }} />

          {/* ── Pre-send checklist modal ── */}
          {showSendChecklist && (
            <div className="overlay" onClick={e=>e.target===e.currentTarget&&setShowSendChecklist(false)}>
              <div className="modal" style={{maxWidth:500}} onClick={e=>e.stopPropagation()}>
                <div className="modal-head">
                  <div>
                    <div className="modal-title">✅ Pre-Send Checklist</div>
                    <div style={{fontSize:12,color:'var(--muted)',marginTop:2}}>Confirm before sending to {client.company_name}</div>
                  </div>
                  <button className="modal-close" onClick={()=>setShowSendChecklist(false)}>✕</button>
                </div>
                <div className="modal-body">
                  {[
                    {label:'Policy details are correct (insurer, policy number, period)', icon:'📋'},
                    {label:'All required documents are attached', icon:'📎'},
                    {label:'Total invoice amount is correct', icon:'💰'},
                    {label:'Key exclusions and conditions have been noted', icon:'⚠️'},
                    {label:retail?'Informed consent for commission confirmed on call':'Duty of disclosure confirmed on call', icon:'⚖️'},
                    ...(claimsMade?[{label:'Claims-made notice given on call',icon:'🔔'}]:[]),
                    ...(coverData.payment_type==='monthly'?[{label:`Premium funding discussed — ${coverData.premium_funder||'funder'} selected`,icon:'📅'}]:[]),
                  ].map((item,i)=>(
                    <div key={i} style={{display:'flex',alignItems:'flex-start',gap:12,padding:'12px 0',borderBottom:'1px solid var(--border)'}}>
                      <span style={{fontSize:20,flexShrink:0}}>{item.icon}</span>
                      <span style={{fontSize:13,lineHeight:1.6,color:'var(--text)'}}>{item.label}</span>
                    </div>
                  ))}
                  <div style={{display:'flex',gap:10,marginTop:20}}>
                    <button className="btn btn-secondary" style={{flex:1}} onClick={()=>setShowSendChecklist(false)}>Go Back</button>
                    <button className="btn btn-primary" style={{flex:2,background:'#1e7a46'}} disabled={sending} onClick={()=>{setShowSendChecklist(false);sendEmail()}}>{sending?'Sending...':'✅ Confirmed — Send Now'}</button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
// ── CoversTab ─────────────────────────────────────────────────────────────────

export function CoversTab({ clientId, onNewCC }) {
  const [records, setRecords] = useState([])
  const [loading, setLoading] = useState(true)
  const [editingRecord, setEditingRecord] = useState(null)
  const [editForm, setEditForm] = useState({})
  const [editSaving, setEditSaving] = useState(false)

  useEffect(() => {
    const load = async () => {
      setLoading(true)
      const { data } = await supabase.from('cover_confirmations').select('*').eq('client_id',clientId).order('created_at',{ascending:false})
      setRecords(data||[])
      setLoading(false)
    }
    load()
  },[clientId])

  const openPdf = (record) => {
    const w = window.open('','_blank','width=1200,height=900')
    if (!w) return
    w.document.write(`<html><head><title>Cover Confirmed</title><style>*{-webkit-print-color-adjust:exact!important;print-color-adjust:exact!important}body{margin:0;background:#f4f4f4}.bar{position:fixed;top:16px;right:16px;z-index:9999;display:flex;gap:8px}.btn{background:#1e3d30;color:#fff;border:none;padding:10px 20px;border-radius:10px;font-size:14px;font-weight:700;cursor:pointer}.cls{background:#6b8278}@media print{.bar{display:none!important}@page{margin:0;size:A4}}</style></head><body><div class="bar"><button class="btn" onclick="window.print()">Print / Save PDF</button><button class="btn cls" onclick="window.close()">Close</button></div>${record.html_preview||''}</body></html>`)
    w.document.close(); w.focus()
  }

  if (loading) return <div className="loading"><div className="spinner"/><span>Loading...</span></div>

  return (
    <div>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:14 }}>
        <div style={{ fontSize:13, fontWeight:600 }}>Cover Confirmations ({records.length})</div>
        <button className="btn btn-orange btn-sm" onClick={onNewCC}>+ New Cover Confirmed</button>
      </div>
      {records.length===0
        ? <div className="empty" style={{ padding:'32px 24px' }}><div className="empty-icon">✅</div><div className="empty-title">No cover confirmations yet</div></div>
        : <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
            {records.map(record => {
              const d = record.cover_data||{}
              const retail = isRetailType(d.cover_type||'')
              const isEndo = !!d.is_endorsement
              return (
                <div key={record.id} style={{ background:'var(--surface2)', border:`1.5px solid ${isEndo ? 'var(--forest-border)' : 'var(--border)'}`, borderRadius:12, padding:'14px 16px', display:'flex', alignItems:'center', justifyContent:'space-between', gap:12 }}>
                  <div style={{ display:'flex', alignItems:'center', gap:12 }}>
                    <div style={{ fontSize:22 }}>{isEndo ? '✏️' : '✅'}</div>
                    <div>
                      <div style={{ fontSize:13, fontWeight:600 }}>{record.title||'Cover Confirmed'}</div>
<div style={{ fontSize:11, color:'var(--muted)', marginTop:2 }}>
                        {d.insurer?`${d.insurer} · `:''}{d.policy_number?`${d.policy_number} · `:''}
                        {isEndo && <span style={{ color:'var(--forest)', fontWeight:600 }}>Endorsement · </span>}
                        <span style={{ color:record.status==='Sent'?'var(--forest)':'var(--muted)', fontWeight:record.status==='Sent'?600:400 }}>{record.status}</span>
                        {` · ${record.created_by||'—'}`}
                        {` · ${retail?'Retail':'Wholesale'}`}
                        {record.created_at ? ` · ${new Date(record.created_at).toLocaleString('en-AU',{day:'numeric',month:'short',year:'numeric',hour:'2-digit',minute:'2-digit'})}` : ''}
                      </div>
                      {(() => {
                        const files = Object.values(d.doc_files || {})
                        if (!files.length) return null
                        return (
                          <div style={{ marginTop:6, display:'flex', flexWrap:'wrap', gap:6 }}>
                            {files.map((f, i) => (
                              <a key={i} href={f.url} target="_blank" rel="noreferrer"
                                style={{ fontSize:11, color:'var(--forest)', textDecoration:'none', padding:'3px 9px', borderRadius:6, background:'var(--forest-subtle)', border:'1px solid var(--forest-border)', fontWeight:600 }}>
                                📎 {f.name}
                              </a>
                            ))}
                          </div>
                        )
                      })()}
                      {isEndo && d.endorsement_description && (
                        <div style={{ fontSize:11, color:'var(--forest)', marginTop:3, fontStyle:'italic', maxWidth:500, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                          {d.endorsement_description}
                        </div>
                      )}
                    </div>
                  </div>
<div style={{display:'flex',gap:6}}>
                    <button className="btn btn-secondary btn-sm" onClick={()=>openPdf(record)}>PDF</button>
                    <button className="btn btn-secondary btn-sm" onClick={(e)=>{e.stopPropagation();setEditingRecord(record)}}>✏️</button>
                    {record.status !== 'Sent'
                      ? <>
                          <button className="btn btn-secondary btn-sm" style={{color:'var(--forest)',borderColor:'var(--forest-border)',background:'var(--forest-subtle)',fontWeight:700,fontSize:11}}
                            onClick={async(e)=>{e.stopPropagation();if(!window.confirm('Mark this record as Sent? This cannot be undone.')) return;await supabase.from('cover_confirmations').update({status:'Sent'}).eq('id',record.id);setRecords(prev=>prev.map(r=>r.id===record.id?{...r,status:'Sent'}:r))}}>
                            ✓ Mark Sent
                          </button>
                          <button className="btn btn-danger btn-sm" onClick={async(e)=>{e.stopPropagation();if(!window.confirm('Delete this draft cover confirmation?')) return;await supabase.from('cover_confirmations').delete().eq('id',record.id);setRecords(prev=>prev.filter(r=>r.id!==record.id))}}>✕</button>
                        </>
                      : <span style={{fontSize:11,color:'var(--forest)',padding:'4px 8px',fontWeight:600}}>🔒 Sent</span>
                    }
                  </div>
                </div>
              )
            })}
          </div>
      }

 
      {editingRecord && (
        <div className="overlay" onClick={e=>e.target===e.currentTarget&&setEditingRecord(null)}>
          <div className="modal" style={{maxWidth:500}}>
            <div className="modal-head">
              <div><div className="modal-title">Edit Commission</div><div style={{fontSize:12,color:'var(--muted)',marginTop:2}}>{editingRecord.title}</div></div>
              <button className="modal-close" onClick={()=>setEditingRecord(null)}>✕</button>
            </div>
            <div className="modal-body">
            {(() => {
                const d = editingRecord.cover_data||{}
                if (!editForm.commission_amount && editForm.commission_amount !== '') {
                  setTimeout(()=>setEditForm({commission_amount:d.commission_amount||'',broker_fee:d.broker_fee||''}),0)
                  return null
                }
                const parseCurrLocal = (v) => (v||'').replace(/[^\d.]/g,'')
                const formatCurrLocal = (v) => { const n=Number(parseCurrLocal(v)); if(!Number.isFinite(n)||n<=0) return ''; return n.toLocaleString('en-AU',{style:'currency',currency:'AUD',minimumFractionDigits:2,maximumFractionDigits:2}) }
                return (
                  <div>
                    <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:14,marginBottom:14}}>
                      <div style={{background:'var(--surface2)',borderRadius:8,padding:'8px 12px'}}><div style={{fontSize:10,fontWeight:600,color:'var(--muted)',textTransform:'uppercase',letterSpacing:'.4px',marginBottom:2}}>Premium</div><div style={{fontSize:14,fontWeight:700}}>{d.total_invoice||'—'}</div></div>
                      <div style={{background:'var(--surface2)',borderRadius:8,padding:'8px 12px'}}><div style={{fontSize:10,fontWeight:600,color:'var(--muted)',textTransform:'uppercase',letterSpacing:'.4px',marginBottom:2}}>Insurer</div><div style={{fontSize:14,fontWeight:700}}>{d.insurer||'—'}</div></div>
                    </div>
                    <div className="form-grid">
                      <div><label className="form-label">Commission Amount ($)</label><input className="form-input" value={editForm.commission_amount} onChange={e=>setEditForm(p=>({...p,commission_amount:parseCurrLocal(e.target.value)}))} onBlur={e=>setEditForm(p=>({...p,commission_amount:formatCurrLocal(e.target.value)}))} onFocus={e=>setEditForm(p=>({...p,commission_amount:parseCurrLocal(e.target.value)}))} placeholder="e.g. 450.00" /></div>
                      <div><label className="form-label">Broker Fee ($)</label><input className="form-input" value={editForm.broker_fee} onChange={e=>setEditForm(p=>({...p,broker_fee:parseCurrLocal(e.target.value)}))} onBlur={e=>setEditForm(p=>({...p,broker_fee:formatCurrLocal(e.target.value)}))} onFocus={e=>setEditForm(p=>({...p,broker_fee:parseCurrLocal(e.target.value)}))} placeholder="e.g. 0.00" /></div>
                    </div>
                    <div className="form-actions">
                      <button className="btn btn-secondary" onClick={()=>{setEditingRecord(null);setEditForm({})}}>Cancel</button>
                      <button className="btn btn-primary" disabled={editSaving} onClick={async()=>{
                        setEditSaving(true)
                        const updatedCoverData = {...(editingRecord.cover_data||{}),commission_amount:editForm.commission_amount,broker_fee:editForm.broker_fee}
                        await supabase.from('cover_confirmations').update({cover_data:updatedCoverData}).eq('id',editingRecord.id)
                        setRecords(prev=>prev.map(r=>r.id===editingRecord.id?{...r,cover_data:updatedCoverData}:r))
                        setEditingRecord(null);setEditForm({});setEditSaving(false)
                      }}>{editSaving?'Saving...':'Save Commission'}</button>
                    </div>
                  </div>
                )
              })()}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
